// Active Deals by loan step (Preview): a row of step counts that jump to their section, and, on phones, the
// step sections stacked with heading and count. Every step shows; empty ones say "None". Built on the stage
// view's look (StageStrip, StageSections); steps carry no colour of their own, the loan's stage chip does.
import type { ReactNode } from "react";
import { NO_STEP_LABEL, stepsReached, type StepSection } from "../lib/loanSteps";
import { STAGE_NOT_SET_LABEL } from "../lib/stageGroups";
import type { Stage } from "../lib/types";
import { cx, StagePill } from "./ui";

export function StepStrip<T>({ sections, onJump, stale, onRegroup }: { sections: StepSection<T>[]; onJump: (key: string) => void; stale: boolean; onRegroup: () => void }) {
  return (
    <nav
      aria-label="Loans by step"
      className="-mx-4 flex items-center gap-1 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden"
    >
      {sections.map((s) => {
        const n = s.items.length;
        const name = s.step ? `${s.step.n} ${s.step.short}` : s.label;
        return (
          <button
            key={s.key}
            onClick={() => onJump(s.key)}
            title={`${s.label}: ${n} loan${n === 1 ? "" : "s"}`}
            aria-label={`${s.label}, ${n} loan${n === 1 ? "" : "s"}`}
            className={cx(
              "focus-ring inline-flex h-11 shrink-0 items-center gap-1.5 rounded-md border px-2 text-[12px] font-semibold whitespace-nowrap md:h-7",
              n === 0 ? "border-subtle bg-surface text-ink-3" : s.step ? "border-line bg-sunken text-ink" : "border-dashed border-line bg-surface text-ink",
            )}
          >
            <span>{name}</span>
            <span className={cx("tnum", n === 0 ? "font-medium" : "font-bold")}>{n}</span>
          </button>
        );
      })}
      {stale ? (
        <button onClick={onRegroup} className="focus-ring ml-1 h-11 shrink-0 rounded-md px-2 text-[12px] font-medium whitespace-nowrap text-link hover:underline md:h-7">
          Stages changed · regroup
        </button>
      ) : null}
    </nav>
  );
}

export function StepSections<T>({ sections, renderItems }: { sections: StepSection<T>[]; renderItems: (items: T[]) => ReactNode }) {
  return (
    <div>
      {sections.map((s) => (
        <section key={s.key} id={`step-section-${s.key}`} aria-labelledby={`step-heading-${s.key}`} className="scroll-mt-[108px]">
          <h2 id={`step-heading-${s.key}`} className="flex items-center gap-2 border-b border-subtle bg-sunken px-4 py-2 text-[12px] font-bold tracking-wide text-ink uppercase">
            <span className="min-w-0 flex-1 truncate">{s.label}</span>
            <span className="tnum text-ink-2">{s.items.length}</span>
          </h2>
          {s.items.length ? (
            renderItems(s.items)
          ) : (
            <p className="border-b border-subtle px-4 py-2 text-[13px] text-ink-3">
              None{s.step?.emptyNote ? <span className="block text-[12px]">{s.step.emptyNote}</span> : null}
            </p>
          )}
        </section>
      ))}
    </div>
  );
}

/**
 * The loan drawer's step tracker (Preview): the steps reached so far, computed from the stage alone. Nothing is
 * claimed done: earlier steps only say the stage is past them, and a step no stage maps to says so.
 */
export function LoanStepTracker({ stageKey, stages }: { stageKey: string | null; stages: Stage[] }) {
  const reached = stepsReached(stageKey);
  const stage = stages.find((s) => s.key === stageKey);
  if (!reached.length) {
    return <p className="text-[13px] text-ink-3">{stage ? `${NO_STEP_LABEL}.` : `${STAGE_NOT_SET_LABEL}, so no step yet.`}</p>;
  }
  return (
    <ol className="space-y-1" aria-label="Loan steps reached">
      {reached.map(({ step, mark }) => (
        <li key={step.key} className="flex min-h-[44px] items-center gap-3 border-b border-subtle py-1.5 last:border-b-0">
          <span
            aria-hidden
            className={cx(
              "flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border text-[12px] font-bold tnum",
              mark === "current" ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-ink-3",
            )}
          >
            {step.n}
          </span>
          <div className="min-w-0 flex-1">
            <div className={cx("text-[14px]", mark === "current" ? "font-semibold text-ink" : "text-ink-2")}>{step.name}</div>
            <div className="text-[12px] text-ink-3">{mark === "current" ? "Current step, from the stage" : mark === "past" ? "Stage is past this step" : "Runs alongside; the stage doesn't track it"}</div>
          </div>
          {mark === "current" && stage ? <StagePill stage={stage} total={stages.length} /> : null}
        </li>
      ))}
    </ol>
  );
}
